import { CommonModule } from '@angular/common';
import { Component, HostBinding, Inject, OnInit } from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  Collaboration,
  CURRENT_PRIVACY_VERSION,
  CURRENT_TERMS_VERSION,
} from '@pncl/common-models';
import { WbDialogService } from 'src/app/services/wb-dialog.service';
import { Intercom } from 'ng-intercom';
import { BehaviorSubject, combineLatest, map, Observable, startWith, switchMap } from 'rxjs';
import { ProfilesService } from 'src/app/services/profiles.service';
import { User } from 'src/app/models/user';
import { nameValidators } from '../../common/utils/form-validators';
import { SVGIconComponentsModule } from '../../common/svg-icon/svg-icon-components.module';
import { roleIDs, roleValueIDs, TeacherRoleDescription } from '../../models/user';
import { UserService } from '../../services/user.service';
import {
  SplitScreenFormBasedComponent,
  SplitScreenModalConfig,
} from '../../standalones/components/modals/split-screen-modals/split-screen-form-based-modal/split-screen-form-based-modal.component';
import {
  RadioGroupInputComponent,
  RadioGroupInputOption,
} from '../../standalones/components/radio-group-input/radio-group-input.component';
import { PencilSearchableDropdownComponent } from '../../standalones/components/pencil-dropdown/pencil-searchable-dropdown.component';
import {
  buildDescriptionOptionsMap,
  buildUserRoleOptions,
  buildStudentStatusOptions,
  RoleDescriptionOption,
} from './user-info-options';
import { USER_INFO_DIALOG_HEADERS, UserInfoDialogMode } from './user-info-dialog-mode';

interface UserInfoDialogData {
  isSpaceRoute?: boolean;
  /** Defaults to asking for name and role only. */
  mode?: UserInfoDialogMode;
}

@UntilDestroy()
@Component({
  selector: 'app-user-info-input-dialog',
  templateUrl: './user-info-input-dialog.component.html',
  styleUrls: ['./user-info-input-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    SplitScreenFormBasedComponent,
    PencilSearchableDropdownComponent,
    RadioGroupInputComponent,
    SVGIconComponentsModule,
  ],
})
export class UserInfoInputDialogComponent implements OnInit {
  // Constants & Configuration
  readonly maxNameLength = Collaboration.Constants.MAX_ENTITY_TITLE_LENGTH;
  readonly roleValueIds = roleValueIDs;
  readonly Mode = UserInfoDialogMode;
  readonly mode: UserInfoDialogMode;
  readonly header: { title: string; subtitle: string };

  @HostBinding('class.legal-agreements-only')
  get isLegalAgreementsOnly(): boolean {
    return this.mode === UserInfoDialogMode.LEGAL_AGREEMENTS;
  }

  // Form & Options
  form: UntypedFormGroup;
  formConfig$: Observable<SplitScreenModalConfig>;

  studentStatusOptions: RadioGroupInputOption[] = [];
  defaultStudentStatus: RadioGroupInputOption | null = null;
  roleOptions: RadioGroupInputOption[] = [];
  descriptionOptions: Record<string, RoleDescriptionOption[]> = {};

  // State Management
  private readonly hasClickedSubmitButton$ = new BehaviorSubject<boolean>(false);
  private readonly isLoading$ = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly dialogRef: MatDialogRef<UserInfoInputDialogComponent>,
    private readonly userService: UserService,
    private profileService: ProfilesService,
    private intercom: Intercom,
    private readonly translateService: TranslateService,
    private wbDialogService: WbDialogService,
    @Inject(MAT_DIALOG_DATA) public readonly data: UserInfoDialogData,
  ) {
    this.wbDialogService.pushNewDialogRef<UserInfoInputDialogComponent>(this.dialogRef);
    this.mode = this.data.mode ?? UserInfoDialogMode.NAME_AND_ROLE;
    this.header = USER_INFO_DIALOG_HEADERS[this.mode];
    this.form = this.initializeForm();
    this.prefillUserName();
    this.formConfig$ = this.createFormConfig();
  }

  ngOnInit(): void {
    this.initializeOptions();
  }

  // Role Management Methods
  /**
   * Handles role selection changes.
   * Business rules:
   * - Resets role description when role changes
   * - Auto-selects "teaching_session" for teachers in space context
   * - Updates validation requirements based on role
   */
  setRoleValue(option: RadioGroupInputOption): void {
    this.form.get('info')?.get('role')?.setValue(option.value);

    const defaultDescription = this.getDefaultRoleDescription(option.value);
    this.resetRoleDescription(defaultDescription);
    this.updateRoleDescriptionValidation(option.value);
  }

  // Handles role description selection.
  setRoleDescription(option: RoleDescriptionOption): void {
    this.getRoleDescriptionControl()?.setValue(option.value);
  }

  // Gets the role description form control.
  private getRoleDescriptionControl(): UntypedFormControl | null {
    return this.form.get('info')?.get('roleDescription') as UntypedFormControl | null;
  }

  // Checks if the given role has description options available.
  private hasRoleDescriptionOptions(roleValue: string): boolean {
    return !!this.descriptionOptions[roleValue] && !this.data.isSpaceRoute;
  }

  /**
   * Returns the default role description for a given role.
   * Business rule: Teachers in space context default to "teaching_session".
   */
  private getDefaultRoleDescription(roleValue: string): string | null {
    const isTeacherInSpace = this.data.isSpaceRoute && roleValue === this.roleValueIds.teacher;
    return isTeacherInSpace ? TeacherRoleDescription.TEACHING_SESSION : null;
  }

  // Resets the role description field to the provided value.
  private resetRoleDescription(value: string | null): void {
    const control = this.getRoleDescriptionControl();
    control?.setValue(value);
    control?.markAsUntouched();
    this.hasClickedSubmitButton$.next(false);
  }

  /**
   * Updates validation rules for role description based on the selected role.
   * Role description is required only if the role has description options.
   */
  private updateRoleDescriptionValidation(roleValue: string): void {
    const control = this.getRoleDescriptionControl();
    if (!control) {
      return;
    }

    if (this.hasRoleDescriptionOptions(roleValue)) {
      control.setValidators([Validators.required]);
    } else {
      control.clearValidators();
    }
    control.updateValueAndValidity();
  }

  // True if the dialog is asking for `target`, alone or together with the other section.
  private isInMode(target: UserInfoDialogMode): boolean {
    return (
      this.mode === target ||
      this.mode === UserInfoDialogMode.NAME_AND_ROLE_AND_LEGAL_AGREEMENTS
    );
  }

  // Form Initialization
  private initializeForm(): UntypedFormGroup {
    // Only the controls for the sections being shown exist, so hidden ones can't block submit.
    const form = new UntypedFormGroup({});

    if (this.isInMode(UserInfoDialogMode.NAME_AND_ROLE)) {
      const defaultRole = this.data.isSpaceRoute ? roleValueIDs.student : '';
      form.addControl('name', new UntypedFormControl('', nameValidators()));
      form.addControl(
        'info',
        new UntypedFormGroup({
          role: new UntypedFormControl(defaultRole, [Validators.required]),
          roleDescription: new UntypedFormControl(''),
          // Hidden: fixed values saved along with the role.
          questionnaireComplete: new UntypedFormControl(true),
          roleType: new UntypedFormControl(roleIDs.education),
        }),
      );
    }

    if (this.isInMode(UserInfoDialogMode.LEGAL_AGREEMENTS)) {
      form.addControl(
        'acceptedLegalAgreements',
        new UntypedFormControl(false, [Validators.requiredTrue]),
      );
      // Hidden: the versions travel silently; the server checks them and stamps the time.
      form.addControl(
        'legalAgreements',
        new UntypedFormGroup({
          termsVersion: new UntypedFormControl(CURRENT_TERMS_VERSION),
          privacyVersion: new UntypedFormControl(CURRENT_PRIVACY_VERSION),
        }),
      );
    }

    return form;
  }

  private initializeOptions(): void {
    this.studentStatusOptions = buildStudentStatusOptions(this.translateService);
    this.roleOptions = buildUserRoleOptions(this.translateService);
    this.descriptionOptions = buildDescriptionOptionsMap(this.translateService);

    if (this.data.isSpaceRoute) {
      this.defaultStudentStatus =
        this.studentStatusOptions.find((opt) => opt.value === this.roleValueIds.student) ?? null;
    }
  }

  private prefillUserName(): void {
    const currentUser = this.userService.user.getValue();
    if (currentUser?.user.name) {
      this.form.get('name')?.setValue(currentUser.user.name);
    }
  }

  // Form Configuration
  private createFormConfig(): Observable<SplitScreenModalConfig> {
    const isFormInvalid$ = this.form.statusChanges.pipe(
      map(() => this.form.invalid),
      startWith(this.form.invalid),
    );

    return combineLatest([isFormInvalid$, this.isLoading$, this.hasClickedSubmitButton$]).pipe(
      map(
        ([isFormInvalid, isLoading, hasClickedSubmitButton]): SplitScreenModalConfig => ({
          submitButtonText: this.translateService.instant('Done'),
          isSubmitDisabled: isFormInvalid && hasClickedSubmitButton,
          isLoading,
          showPreview: true,
          fullScreen: true,
          formLoadingText: this.translateService.instant(
            this.isInMode(UserInfoDialogMode.NAME_AND_ROLE)
              ? 'Updating your profile...'
              : 'Saving your agreement...',
          ),
          formLoadingSubtext: this.translateService.instant('This may take a few seconds'),
        }),
      ),
    );
  }

  // Form Submission
  handleSubmit(): void {
    if (this.form.invalid) {
      this.handleInvalidForm();
      return;
    }

    this.submitForm();
  }

  private handleInvalidForm(): void {
    this.hasClickedSubmitButton$.next(true);
    this.form.markAllAsTouched();
  }

  private submitForm(): void {
    const formData = this.buildFormData();
    const updatedUser = this.buildUpdatedUser(formData);
    const roleDescription = this.form.value.info?.roleDescription;

    this.isLoading$.next(true);
    this.profileService
      .updateCurrentUserGeneralInfo(updatedUser)
      .pipe(
        switchMap(() => this.profileService.getCurrentUserGeneralInfo()),
        untilDestroyed(this),
      )
      .subscribe({
        next: (response: { user: User }) => this.handleSubmitSuccess(response, roleDescription),
        error: (error) => this.handleSubmitError(error),
        complete: () => this.isLoading$.next(false),
      });
  }

  // Sections that aren't shown have no controls, so their entries are undefined and left out
  // of the request. The checkbox and the role description only drive the UI.
  private buildFormData(): any {
    const { name, info, legalAgreements } = this.form.value;
    return {
      name: name?.trim(),
      info: info && { ...info, roleDescription: undefined },
      legalAgreements,
    };
  }

  private buildUpdatedUser(formData: any): User {
    // The legal-agreements-only request must not resend the name and role (it would overwrite).
    if (!this.isInMode(UserInfoDialogMode.NAME_AND_ROLE)) {
      return formData;
    }
    const currentUser = this.userService.user.getValue()!.user;
    return { ...currentUser, ...formData };
  }

  private handleSubmitSuccess(response: { user: User }, roleDescription?: string): void {
    if (this.isInMode(UserInfoDialogMode.NAME_AND_ROLE)) {
      const userInfo: Record<string, unknown> = { ...response.user.info };
      if (roleDescription) {
        userInfo.roleDescription = roleDescription;
      }
      this.intercom.update({ userInfo });
    }
    this.dialogRef.close({
      ...response,
      roleDescription,
      legalAgreements: response.user.legalAgreements,
    });
  }

  private handleSubmitError(error: any): void {
    console.error('Error updating user info:', error);
    this.dialogRef.close(null);
  }
}
